// src/pwa/installPrompt.js
//
// "Add this web page to the home screen" plumbing for the two badges in
// PromoPill (Apple App Store + Google Play styled).
//
// THE HARD TRUTH about A2HS (add-to-home-screen), baked into these helpers:
//   • Android / Chrome  → real, near-native install. Chrome fires a
//     `beforeinstallprompt` event we stash here; the badge later calls
//     promptInstall() to show the system dialog. REQUIRES the site to be
//     "installable" (valid manifest + a registered service worker — see
//     public/sw.js and registerPwaServiceWorker() below).
//   • iOS / Safari      → NO programmatic install exists. Apple blocks it.
//     The Apple badge can only open a coach-mark ("Share → Add to Home
//     Screen"). Only Safari can do it; Chrome/Firefox on iOS cannot.
//
// This module attaches its listeners at import time (import it early, e.g.
// from index.js) so the event isn't missed, and exposes a tiny pub/sub so
// React components can re-render when the captured prompt appears/clears.

let deferredPrompt = null;
const listeners = new Set();

function emit() {
  listeners.forEach((fn) => {
    try { fn(); } catch {}
  });
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    // Stop Chrome's default mini-infobar; we drive the install from our badge.
    e.preventDefault();
    deferredPrompt = e;
    emit();
  });
  window.addEventListener("appinstalled", () => {
    // Prompt is single-use and now moot.
    deferredPrompt = null;
    emit();
  });
}

/** Subscribe to prompt-availability changes. Returns an unsubscribe fn. */
export function subscribeInstall(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** True when Chrome/Android has handed us a usable install prompt. */
export function canPromptInstall() {
  return !!deferredPrompt;
}

/**
 * Fire the native Android/Chrome install dialog. One-shot: Chrome invalidates
 * the event after use, so we clear it and notify subscribers either way.
 * Returns { outcome: "accepted" | "dismissed" | "unavailable" | "error" }.
 */
export async function promptInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  const evt = deferredPrompt;
  deferredPrompt = null;
  emit();
  try {
    await evt.prompt();
    const choice = await evt.userChoice;
    return { outcome: choice?.outcome || "accepted" };
  } catch {
    return { outcome: "error" };
  }
}

/** Already running as an installed app (either platform)? Then hide the badges. */
export function isStandalone() {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia && window.matchMedia("(display-mode: standalone)");
  return !!(mm && mm.matches) || window.navigator.standalone === true; // last: iOS Safari
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const classic = /iPad|iPhone|iPod/.test(ua);
  // iPadOS 13+ masquerades as desktop Safari; sniff a touch-capable "Mac".
  const iPadOS = navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1;
  return classic || iPadOS;
}

export function isAndroid() {
  if (typeof navigator === "undefined") return false;
  return /Android/i.test(navigator.userAgent || "");
}

/** Only real Safari can A2HS on iOS — Chrome (CriOS)/Firefox (FxiOS) cannot. */
export function isIOSSafari() {
  if (!isIOS()) return false;
  const ua = navigator.userAgent || "";
  return !/CriOS|FxiOS|EdgiOS|OPiOS|Mercury/.test(ua);
}

/** Coarse "is this a phone/tablet" check — badges are mobile-only. */
export function isTouchDevice() {
  if (typeof window === "undefined") return false;
  try {
    if (window.matchMedia && window.matchMedia("(pointer: coarse)").matches) return true;
  } catch {}
  return "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
}

/**
 * Register the minimal service worker (public/sw.js). Without it, Android
 * Chrome will NOT consider the site installable and `beforeinstallprompt`
 * never fires. Safe on http://localhost and any https origin; a no-op on
 * insecure origins (where SWs are disallowed anyway). Call once at boot.
 */
export function registerPwaServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  const register = () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      // Non-fatal: install badge just won't light up on Android.
      try { console.warn("[pwa] service worker registration failed:", err); } catch {}
    });
  };
  if (document.readyState === "complete") {
    register();
  } else {
    window.addEventListener("load", register, { once: true });
  }
}
